import React, { useEffect, useState } from 'react';
import { CheckCircle2, Play } from 'lucide-react';
import { workspaceAPI } from '../../api/client';
import { useTenant } from '../../context/TenantContext';
import { apiError } from '../../lib/host';
import { ComingSoon, ErrorBanner, PageHeading, StatCards, useBasic } from './ui';

const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const CAPTURE = [['name', 'Name'], ['phone', 'Phone number'], ['email', 'Email'], ['requirement', 'Requirement'], ['budget', 'Budget'], ['preferred_time', 'Preferred call-back time']];
const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Asia/Kathmandu', 'Asia/Dhaka', 'Asia/Colombo', 'Europe/London', 'America/New_York', 'Australia/Sydney', 'UTC'];
const HANDOFF = {
  never: 'Never: the AI handles every call',
  when_asked: 'When the caller asks for a person',
  after_hours_only: 'AI answers only outside business hours',
  always: 'Always try a person first',
};

function Chips({ options, value, onChange, disabled }) {
  const toggle = (key) => onChange(value.includes(key) ? value.filter((v) => v !== key) : [...value, key]);
  return (
    <div className="chip-row">
      {options.map(([key, label]) => (
        <button type="button" key={key} disabled={disabled} aria-pressed={value.includes(key)}
          className={`chip ${value.includes(key) ? 'on' : ''}`} onClick={() => toggle(key)}>{label}</button>
      ))}
    </div>
  );
}

export default function AiReceptionistPage() {
  const { notify } = useBasic();
  const { can, hasFeature } = useTenant();
  const canEdit = can('settings.write');
  // Without the AI receptionist (Feature Access Control) the page sets how calls are answered: greeting, office hours
  // and after-hours message. The AI's own options are left out; the API keeps them unchanged.
  const ai = hasFeature('ai_receptionist');
  const title = ai ? 'AI Receptionist' : 'Call Answering';
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    workspaceAPI.aiSettings().then((res) => { setSaved(res.data); setForm(res.data); })
      .catch((err) => setError(apiError(err, 'Could not load AI receptionist settings')));
  }, []);

  if (!form) return <section className="page-view"><PageHeading title={title} subtitle="Loading…" /><ErrorBanner message={error} /></section>;

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const dirty = JSON.stringify(form) !== JSON.stringify(saved);

  const save = async (e) => {
    e.preventDefault();
    if (form.working_days.length === 0) {
      setError('Choose at least one working day.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await workspaceAPI.saveAiSettings(form);
      setSaved(res.data);
      setForm(res.data);
      notify(ai ? 'AI receptionist settings saved.' : 'Call answering settings saved.');
    } catch (err) {
      setError(apiError(err, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  const dayNames = DAYS.filter(([k]) => saved.working_days.includes(k)).map(([, l]) => l);

  return (
    <section className="page-view">
      <PageHeading title={title} subtitle={ai ? 'Configure your AI agent, greeting, and call-handling behaviour.' : 'Your greeting, business hours and what callers hear when you are closed.'}>
        {ai && <button className="secondary-action" onClick={() => notify('Test calls to the AI receptionist are coming soon.')}><Play size={13} />Test AI receptionist</button>}
      </PageHeading>
      <ErrorBanner message={error} />

      <StatCards stats={ai ? [
        ['AI status', saved.enabled ? 'On' : 'Off', saved.enabled ? 'Answering when calling goes live' : 'Paused'],
        ['Receptionist name', saved.name, 'Introduces itself as'],
        ['Business hours', `${saved.opens_at} – ${saved.closes_at}`, dayNames.length === 7 ? 'Every day' : dayNames.join(', ')],
        ['Details collected', saved.capture.length, 'From each caller'],
      ] : [
        ['Business hours', `${saved.opens_at} – ${saved.closes_at}`, dayNames.length === 7 ? 'Every day' : dayNames.join(', ')],
        ['Time zone', saved.timezone.replace('_', ' '), 'For your business hours'],
        ['AI receptionist', 'Not in plan', 'Available on higher plans'],
      ]} />

      <div className="ai-settings-grid">
        <form className="panel page-info-card" onSubmit={save}>
          <div className="panel-heading"><h2>{ai ? 'Receptionist settings' : 'Call answering'}</h2>{!canEdit && <span className="cell-muted">Only admins can change these</span>}</div>
          <fieldset className="form-grid" disabled={!canEdit}>
            {ai && (
              <>
                <label className="field toggle-field span-2">
                  <input type="checkbox" checked={form.enabled} onChange={set('enabled')} />
                  <span><b>AI receptionist on</b><small>Answer calls with AI once calling is connected</small></span>
                </label>
                <label className="field">Receptionist name<input required maxLength={80} value={form.name} onChange={set('name')} /></label>
                <label className="field">Hand over to a person
                  <select value={form.handoff} onChange={set('handoff')}>{Object.entries(HANDOFF).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </label>
              </>
            )}
            <label className="field span-2">Greeting<textarea rows={2} required maxLength={500} value={form.greeting} onChange={set('greeting')} /></label>
            <div className="field span-2">Working days<Chips options={DAYS} value={form.working_days} onChange={(v) => setForm({ ...form, working_days: v })} disabled={!canEdit} /></div>
            <label className="field">Opens at<input type="time" required value={form.opens_at} onChange={set('opens_at')} /></label>
            <label className="field">Closes at<input type="time" required value={form.closes_at} onChange={set('closes_at')} /></label>
            <label className="field span-2">Time zone for business hours
              <select value={form.timezone} onChange={set('timezone')}>
                {[...new Set([form.timezone, ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
              </select>
            </label>
            <label className="field span-2">After-hours message<textarea rows={2} maxLength={500} value={form.after_hours_message} onChange={set('after_hours_message')} /></label>
            {ai && (
              <>
                <div className="field span-2">Collect from callers<Chips options={CAPTURE} value={form.capture} onChange={(v) => setForm({ ...form, capture: v })} disabled={!canEdit} /></div>
                <label className="field toggle-field span-2">
                  <input type="checkbox" checked={form.book_appointments} onChange={set('book_appointments')} />
                  <span><b>Book appointments</b><small>Let the AI offer meeting slots during the call</small></span>
                </label>
              </>
            )}
          </fieldset>
          {canEdit && (
            <div className="form-actions">
              <button type="button" className="secondary-action" disabled={!dirty || saving} onClick={() => setForm(saved)}>Discard changes</button>
              <button type="submit" className="primary-action" disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save settings'}</button>
            </div>
          )}
        </form>

        <aside className="panel page-info-card ai-preview">
          <div className="panel-heading"><h2>Preview</h2>{ai && <span className={`active-pill ${form.enabled ? '' : 'off'}`}><i />{form.enabled ? 'On' : 'Off'}</span>}</div>
          <div className="preview-bubble"><small>{ai ? `${form.name || 'Receptionist'} says` : 'Callers hear'}</small><p>“{form.greeting}”</p></div>
          <div className="preview-bubble muted"><small>Outside {form.opens_at}–{form.closes_at}</small><p>“{form.after_hours_message}”</p></div>
          {ai && <div className="ai-checks">
            <b>On each call it will</b>
            {form.capture.map((k) => <span key={k}><CheckCircle2 size={13} fill="#10b981" color="#fff" />Ask for {CAPTURE.find(([c]) => c === k)[1].toLowerCase()}</span>)}
            {form.book_appointments && <span><CheckCircle2 size={13} fill="#10b981" color="#fff" />Offer to book an appointment</span>}
            <span><CheckCircle2 size={13} fill="#10b981" color="#fff" />{HANDOFF[form.handoff]}</span>
          </div>}
          {ai
            ? <p className="access-copy">Today incoming calls ring your team (see Phone Numbers), and outside business hours callers hear the after-hours message and can leave a voicemail. The AI itself answering calls is <ComingSoon /></p>
            : <p className="access-copy">Incoming calls ring your team (see Phone Numbers); outside business hours callers hear the after-hours message and can leave a voicemail. An AI receptionist is available on higher plans.</p>}
        </aside>
      </div>
    </section>
  );
}
